import { formatDateToLocale } from "@/common/helpers/format-date";
import { CopyToClipboard } from "@/components/copy-to-clipboard/copy-to-clipboard";
import { Calendar, Hash, Settings } from "lucide-react";
import { useTranslation } from "react-i18next";

export enum SystemInfoIdentifierType {
  TechnicalId = "technicalId",
  V1Id = "v1Id",
}

export type SystemInfoIdentifierLabel =
  | {
      kind: "custom";
      text: string;
    }
  | {
      kind: "known";
      type: SystemInfoIdentifierType;
    };

export interface SystemInfoIdentifier {
  value: string | number;
  label: SystemInfoIdentifierLabel;
}

const identifierLabelTranslationKeys = {
  [SystemInfoIdentifierType.TechnicalId]: "common:TechnicalId",
  [SystemInfoIdentifierType.V1Id]: "masterdata:V1ID",
} as const satisfies Record<SystemInfoIdentifierType, string>;

interface SystemInfoFieldsProps {
  identifiers?: ReadonlyArray<SystemInfoIdentifier>;
  uuid?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
  deletedAt?: string | null;
  orientation?: "horizontal" | "vertical";
}

export function SystemInfoFields({
  identifiers = [],
  uuid,
  createdAt,
  updatedAt,
  deletedAt,
  orientation = "vertical",
}: SystemInfoFieldsProps) {
  const { t } = useTranslation(["common", "masterdata"]);

  if (identifiers.length === 0 && !uuid && !createdAt && !updatedAt && !deletedAt) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="bg-primary/5 border-primary/10 animate-in fade-in slide-in-from-top-2 rounded-lg border p-4 transition-all">
        <h3 className="text-primary mb-4 flex items-center gap-2 text-sm font-semibold">
          <Settings className="h-4 w-4" />
          {t("masterdata:SystemIdentifiers")}
        </h3>
        <div className={orientation === "horizontal" ? "grid grid-cols-1 gap-4 md:grid-cols-3" : "flex flex-col gap-4"}>
          {identifiers.map(({ label, value }) => {
            const identifierValue = String(value);
            const labelText = label.kind === "custom" ? label.text : t(identifierLabelTranslationKeys[label.type]);

            return (
              <div
                className="space-y-1"
                key={`${label.kind === "custom" ? `custom:${label.text}` : label.type}:${identifierValue}`}
              >
                <div className="text-muted-foreground flex items-center gap-2 text-xs">
                  <Hash className="h-3 w-3" />
                  <span className="text-card-foreground font-semibold">{labelText}</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-medium">
                  {identifierValue}
                  <CopyToClipboard value={identifierValue} />
                </div>
              </div>
            );
          })}
          {uuid && (
            <div className="space-y-1">
              <div className="text-muted-foreground flex items-center gap-2 text-xs">
                <Hash className="h-3 w-3" />
                <span className="text-card-foreground font-semibold">{t("common:UUID")}</span>
              </div>
              <div className="flex items-center gap-2 text-sm font-medium">
                {uuid}
                <CopyToClipboard value={uuid} />
              </div>
            </div>
          )}
          {createdAt && (
            <div className="space-y-1">
              <div className="text-muted-foreground flex items-center gap-2 text-xs">
                <Calendar className="h-3 w-3" />
                <span className="text-card-foreground font-semibold">{t("common:CreatedAt")}</span>
              </div>
              <div className="text-sm font-medium">{formatDateToLocale(createdAt)}</div>
            </div>
          )}
          {updatedAt && (
            <div className="space-y-1">
              <div className="text-muted-foreground flex items-center gap-2 text-xs">
                <Calendar className="h-3 w-3" />
                <span className="text-card-foreground font-semibold">{t("common:UpdatedAt")}</span>
              </div>
              <div className="text-sm font-medium">{formatDateToLocale(updatedAt)}</div>
            </div>
          )}
          {deletedAt && (
            <div className="space-y-1">
              <div className="text-muted-foreground flex items-center gap-2 text-xs">
                <Calendar className="h-3 w-3" />
                <span className="text-card-foreground font-semibold">{t("common:Deleted")}</span>
              </div>
              <div className="text-sm font-medium">{formatDateToLocale(deletedAt)}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
